import type { HTMLAttributes } from 'react';
import React from 'react';
import qs from 'qs';
import { get } from 'lodash';
import { Image } from '$components/basic/Image';
import type { PartnerDataProps } from '../types';

export interface PartnerTextProps extends HTMLAttributes<HTMLElement> {
  readonly pathname?: string;
  readonly data?: PartnerDataProps;
}

/**
 *
 * Partners LPs:
 * https://docs.google.com/spreadsheets/d/13Yfsn-HEChAR1SSmFXkFJQ4_E7LABXArpKo2Nvsdkn4/edit#gid=0
 */

export function PartnerText({ pathname, data }: PartnerTextProps): JSX.Element {
  const paramsString = process.browser ? window.location.search : '';

  if (data?.description) {
    const { description, logoUrl, name } = data;
    return (
      <>
        {description} with&nbsp;
        {logoUrl ? (
          <Image
            className={`PartnerImg dynamic ${name}`}
            provider="cloudinary"
            src={logoUrl}
            alt={name || ''}
            layout="fill"
            fill="natural"
          />
        ) : (
          name
        )}
      </>
    );
  }
  switch (pathname) {
    case 'esure':
      return (
        <>
          Get your £19 MOT with{' '}
          <Image
            className="PartnerImg esure"
            provider="cloudinary"
            src="/partners/logo_esure_bpy6vk.jpg"
            alt="esure"
            layout="fixed"
            width="90"
            height="22"
          />
        </>
      );
    case 'sheilas-wheels':
      return (
        <>
          Get your £19 MOT with{' '}
          <Image
            className="PartnerImg sheilas-wheels"
            provider="cloudinary"
            src="/partners/sheilas-wheels-logo_ltjsxj.png"
            alt="sheilas-wheels"
            layout="fixed"
            width="200"
            height="27"
          />
        </>
      );
    case 'getaround':
      return (
        <>
          Get 10% off your booking with{' '}
          <Image
            className="PartnerImg getaround"
            provider="cloudinary"
            src="/partners/logo_getaround_pmsqr5.jpg"
            alt="getaround"
            layout="fixed"
            width="200"
            height="27"
          />
        </>
      );
    case 'caranalytics':
      return (
        <>
          Save £10 on your bookings with{' '}
          <Image
            className="PartnerImg caranalytics"
            provider="cloudinary"
            src="/partners/caranalytics-logo_yxoxto.png"
            alt="caranalytics"
            layout="fixed"
            width="120"
            height="35"
          />
        </>
      );
    case 'perkbox':
      return (
        <>
          Save 50% on your MOT with{' '}
          <Image
            className="PartnerImg perkbox"
            provider="cloudinary"
            src="/partners/perkbox-logo_r1gitb.png"
            alt="perkbox"
            layout="fixed"
            width="120"
            height="29"
          />
        </>
      );
    case 'cartaxcheck':
      return (
        <>
          Save £10 on your bookings with the code <span className="code">CTC10</span>
        </>
      );
    case 'boundless': {
      const params = qs.parse(paramsString, { ignoreQueryPrefix: true });
      const campaignId = get(params, 'campaignId', '');

      switch (campaignId) {
        case '51617d79-27e9-41b1-9ba5-68c7223d71c9':
          return (
            <>
              Book a service and enjoy a half-price MOT with{' '}
              <Image
                className="PartnerImg boundless"
                provider="cloudinary"
                src="/partners/boundless-logo_dn5fjq.png"
                alt="boundless"
                layout="fixed"
                width="120"
                height="49"
              />
            </>
          );
        case 'ea8b4686-b5e2-4725-85ea-b4b574c1643d':
          return (
            <>
              Enjoy 10% off your booking with{' '}
              <Image
                className="PartnerImg boundless"
                provider="cloudinary"
                src="/partners/boundless-logo_dn5fjq.png"
                alt="boundless"
                layout="fixed"
                width="120"
                height="49"
              />
            </>
          );
        default:
          return (
            <>
              Enjoy your special discount with{' '}
              <Image
                className="PartnerImg boundless"
                provider="cloudinary"
                src="/partners/boundless-logo_dn5fjq.png"
                alt="boundless"
                layout="fixed"
                width="120"
                height="49"
              />
            </>
          );
      }
    }
    case 'unidays':
      return (
        <>
          Save 50% on your MOT when booking a service with{' '}
          <Image
            className="PartnerImg unidays"
            provider="cloudinary"
            src="/partners/unidays-logo_qkbnue.svg"
            alt="unidays"
            layout="fixed"
            width="90"
            height="16"
          />
        </>
      );
    case 'parliament-hill': {
      const params = qs.parse(paramsString, { ignoreQueryPrefix: true });
      const campaignId = get(params, 'campaignId', '');

      switch (campaignId) {
        case '5059c97a-bd1d-4733-9515-e76e48a9c3f4':
          return (
            <>
              Enjoy half-price MOT when you book a full service with{' '}
              <span className="code">Parliament Hill</span>
            </>
          );
        case 'b918f9ab-4da8-406e-a439-54fedef4299e':
          return (
            <>
              Enjoy 10% off your booking with <span className="code">Parliament Hill</span>
            </>
          );
        default:
          return (
            <>
              Enjoy your special discount with <span className="code">Parliament Hill</span>
            </>
          );
      }
    }
    case 'kapten': {
      const params = qs.parse(paramsString, { ignoreQueryPrefix: true });
      const tier = get(params, 'tier', '');

      switch (tier) {
        case 'Silver':
          return (
            <>
              Save £5 on your bookings with{' '}
              <Image
                className="PartnerImg kapten"
                provider="cloudinary"
                src="/partners/kapten-logo_wmgjk9.png"
                alt="kapten"
                layout="fixed"
                width="140"
                height="51"
              />
            </>
          );
        case 'Gold':
          return (
            <>
              Save 6% off on your bookings with{' '}
              <Image
                className="PartnerImg kapten"
                provider="cloudinary"
                src="/partners/kapten-logo_wmgjk9.png"
                alt="kapten"
                layout="fixed"
                width="140"
                height="51"
              />
            </>
          );
        case 'Platinum':
          return (
            <>
              Save 12.5% off on your bookings with{' '}
              <Image
                className="PartnerImg kapten"
                provider="cloudinary"
                src="/partners/kapten-logo_wmgjk9.png"
                alt="kapten"
                layout="fixed"
                width="140"
                height="51"
              />
            </>
          );
        default:
          return (
            <>
              Enjoy your special discount with{' '}
              <Image
                className="PartnerImg kapten"
                provider="cloudinary"
                src="/partners/kapten-logo_wmgjk9.png"
                alt="kapten"
                layout="fixed"
                width="140"
                height="51"
              />
            </>
          );
      }
    }
    default:
      return <>Save 10% off on your bookings</>;
  }
}

PartnerText.displayName = 'PartnerText';
